import { useEffect, useRef, useState } from "react";
import { PaperPlaneRight, ArrowCounterClockwise, Stop } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, streamChat } from "@/lib/api";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { AgentAvatar } from "@/components/agent/AgentCard";

export const ChatPanel = ({ agent, conversationId, onConversationCreated, test = false, beforeSend, onMeta, onBlocked, disabled, disabledNode, title, className = "" }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const convRef = useRef(conversationId || null);
  const skipLoad = useRef(null);
  const abortRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    convRef.current = conversationId || null;
    if (!conversationId) {
      setMessages([]);
      return;
    }
    if (skipLoad.current === conversationId) return;
    api.get(`/conversations/${conversationId}/messages`).then((r) => setMessages(r.data)).catch(() => setMessages([]));
  }, [conversationId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const patchLast = (fn) => setMessages((m) => [...m.slice(0, -1), fn(m[m.length - 1])]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    if (beforeSend && (await beforeSend()) === false) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", streaming: true }]);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      await streamChat(
        { agent_id: agent.agent_id, message: msg, conversation_id: convRef.current, test },
        {
          signal: ctrl.signal,
          onMeta: (m) => {
            if (!convRef.current) {
              convRef.current = m.conversation_id;
              skipLoad.current = m.conversation_id;
              onConversationCreated?.(m.conversation_id);
            }
            patchLast((l) => ({ ...l, sources: m.sources }));
            onMeta?.(m);
          },
          onDelta: (d) => patchLast((l) => ({ ...l, content: l.content + d })),
          onError: (e) => patchLast((l) => ({ ...l, error: e })),
        }
      );
    } catch (e) {
      if (e.name === "AbortError") {
        patchLast((l) => ({ ...l, streaming: false }));
      } else {
        setMessages((m) => m.slice(0, -2));
        setInput(msg);
        if (e.status === 402 || e.status === 403) onBlocked?.(e.message);
        toast.error(e.message);
      }
    } finally {
      patchLast((l) => (l?.role === "assistant" ? { ...l, streaming: false } : l));
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const reset = () => {
    convRef.current = null;
    skipLoad.current = null;
    setMessages([]);
  };

  return (
    <div className={`flex h-full min-h-0 flex-col bg-[#080808] ${className}`} data-testid="chat-panel">
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-white/10 px-5">
        <div className="flex min-w-0 items-center gap-3">
          <span className={`h-1.5 w-1.5 ${streaming ? "animate-pulse bg-cyan-400" : "bg-emerald-500"}`} />
          <p className="truncate text-xs font-semibold uppercase tracking-[0.16em] text-zinc-400">{title || agent?.name}</p>
        </div>
        {test && (
          <button onClick={reset} className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white" data-testid="chat-reset-button">
            <ArrowCounterClockwise size={14} /> Reiniciar
          </button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col justify-end gap-6">
            <div>
              <AgentAvatar icon={agent?.icon} color={agent?.color} size={48} />
              <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">{agent?.name}</h3>
              <p className="mt-1 text-sm text-zinc-500">{agent?.tagline || "Escribe un mensaje para empezar."}</p>
            </div>
            {agent?.conversation_starters?.length > 0 && (
              <div className="flex flex-col gap-px border border-white/10 bg-white/10">
                {agent.conversation_starters.map((s, i) => (
                  <button key={i} disabled={disabled} onClick={() => send(s)} className="bg-[#0b0b0b] px-4 py-3 text-left text-sm text-zinc-300 hover:bg-[#151515] hover:text-white disabled:opacity-40" data-testid={`chat-starter-${i}`} style={{ transition: "background-color 160ms" }}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {messages.map((m, i) => <ChatMessage key={i} msg={m} agent={agent} index={i} />)}
            <div ref={endRef} />
          </div>
        )}
      </div>
      <div className="shrink-0 border-t border-white/10 p-4">
        {disabled ? (
          disabledNode
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2 border border-white/10 bg-[#0d0d0d] p-2 focus-within:border-blue-500" style={{ transition: "border-color 200ms" }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder={`Escribe a ${agent?.name?.split("·")[0]?.trim() || "tu agente"}…`}
              className="max-h-40 min-h-[40px] flex-1 resize-none bg-transparent px-2 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none"
              data-testid="chat-input"
            />
            {streaming ? (
              <button type="button" onClick={() => abortRef.current?.abort()} className="btn-ghost !px-3" data-testid="chat-stop-button"><Stop size={16} weight="fill" /></button>
            ) : (
              <button type="submit" disabled={!input.trim()} className="btn-primary !px-3" data-testid="chat-send-button"><PaperPlaneRight size={16} weight="fill" /></button>
            )}
          </form>
        )}
      </div>
    </div>
  );
};
